import * as THREE from 'three';
import { Assets, Texture, Sprite, Spritesheet } from 'pixi.js';
import { GLTF, GLTFLoader } from 'three/examples/jsm/Addons.js';
import { AudioAssets, ModelAssets, PixiAssets, TextureAssets } from '../Data/Configs/Assets/Assets';
import { prefersReducedMotion } from '../utils/motion-preference';

type Asset = THREE.Texture | THREE.Audio | THREE.Object3D | Texture | Sprite | GLTF | AudioBuffer | Spritesheet;

const loadingPercentElement: Element = document.querySelector('.loading-percent');
const ROSE_DRAW_FALLBACK_MS = 1500;
const REVEAL_FADE_MS = 700;

export default class Loader {
  static assets: { [key: string]: Asset } = {};

  private threeJSManager: THREE.LoadingManager;
  private assetsLoaded: boolean = false;
  private roseDrawComplete: boolean = false;
  private hasRevealed: boolean = false;

  constructor() {
    this.threeJSManager = new THREE.LoadingManager(this.onThreeJSAssetsLoaded, this.onThreeJSAssetsProgress);

    this.initRoseAnimationListener();
    this.loadPixiAssets();
  }

  private loadPixiAssets(): void {
    const texturesNames: string[] = [];

    PixiAssets.forEach((assetFilename: string) => {
      const assetName = assetFilename.replace(/\.[^/.]+$/, "");

      Assets.add({ alias: assetName, src: `/${assetFilename}` });
      texturesNames.push(assetName);
    });

    const texturesPromise: Promise<Record<string, Texture>> = Assets.load(texturesNames);

    texturesPromise.then((textures: { [key in string]: Texture }) => {
      texturesNames.forEach((name: string) => {
        this.onAssetLoad(textures[name], name);
      });

      this.loadThreeJSAssets();
    });
  }

  private loadThreeJSAssets(): void {
    this.loadTextures();
    this.loadModels();
    this.loadAudio();

    if (TextureAssets.length === 0 && ModelAssets.length === 0) {
      this.onThreeJSAssetsLoaded();
    }
  }

  private onThreeJSAssetsLoaded = (): void => {
    this.assetsLoaded = true;
    this.checkLoadingGate();

    setTimeout(() => {
      document.dispatchEvent(new Event('onLoad'));
    }, 100);
  }

  private onThreeJSAssetsProgress(): void {}

  private initRoseAnimationListener(): void {
    if (prefersReducedMotion()) {
      this.roseDrawComplete = true;
      return;
    }

    const rosePaths = loadingPercentElement.querySelectorAll('.sig-rose .rp');

    if (rosePaths.length === 0) {
      this.roseDrawComplete = true;
      return;
    }

    const finishedPaths = new Set<Element>();

    rosePaths.forEach((path: Element) => {
      path.addEventListener('animationend', () => {
        finishedPaths.add(path);

        if (finishedPaths.size === rosePaths.length) {
          this.roseDrawComplete = true;
          this.checkLoadingGate();
        }
      }, { once: true });
    });

    setTimeout(() => {
      this.roseDrawComplete = true;
      this.checkLoadingGate();
    }, ROSE_DRAW_FALLBACK_MS);
  }

  private checkLoadingGate(): void {
    if (this.hasRevealed || !this.assetsLoaded || !this.roseDrawComplete) {
      return;
    }

    this.hasRevealed = true;

    loadingPercentElement.classList.add('ended');

    document.dispatchEvent(new Event('onAudioLoaded'));
    document.dispatchEvent(new Event('onReadyToReveal'));

    const revealDelay = prefersReducedMotion() ? 0 : REVEAL_FADE_MS;

    setTimeout(() => {
      document.dispatchEvent(new Event('onLoadingComplete'));
      (loadingPercentElement as HTMLElement).style.display = 'none';
    }, revealDelay);
  }

  private loadTextures(): void {
    const textureLoader = new THREE.TextureLoader(this.threeJSManager);

    const texturesBasePath: string = '/textures/';

    TextureAssets.forEach((textureFilename: string) => {
      const textureFullPath: string = `${texturesBasePath}${textureFilename}`;
      const textureName: string = textureFilename.replace(/\.[^/.]+$/, "");
      Loader.assets[textureName] = textureLoader.load(textureFullPath);
    });
  }

  private loadModels(): void {
    const gltfLoader = new GLTFLoader(this.threeJSManager);

    const modelsBasePath: string = '/models/';

    ModelAssets.forEach((modelFilename: string) => {
      const modelFullPath: string = `${modelsBasePath}${modelFilename}`;
      const modelName: string = modelFilename.replace(/\.[^/.]+$/, "");
      gltfLoader.load(modelFullPath, (gltfModel: GLTF) => this.onAssetLoad(gltfModel, modelName));
    });
  }

  private loadAudio(): void {
    const audioLoader = new THREE.AudioLoader(this.threeJSManager);

    const audioBasePath: string = '/audio/';

    AudioAssets.forEach((audioFilename: string) => {
      const audioFullPath: string = `${audioBasePath}${audioFilename}`;
      const audioName: string = audioFilename.replace(/\.[^/.]+$/, "");
      audioLoader.load(audioFullPath, (audioBuffer: AudioBuffer) => this.onAssetLoad(audioBuffer, audioName));
    });
  }

  onAssetLoad(asset: Asset, name: string) {
    Loader.assets[name] = asset;
  }
}
